/**
 * youtube-hd-thumbnails.js  (v2.2 — deferred load, warm connection,
 *                            history-safe so the Back button works normally)
 * ------------------------------------------------------------------
 * Fixes blurry YouTube poster thumbnails on embeds that load inside a
 * HIDDEN container (closed product tab, collapsible, drawer, etc.).
 *
 * Root cause of the blur: a YouTube iframe that loads while its container is
 * display:none has no measurable size, so YouTube serves a low-res poster,
 * which then sticks even after the tab is revealed.
 *
 * Strategy:
 *   - Iframes hidden at load: remove the src so the blurry poster NEVER loads,
 *     then load the real src the first time the container is revealed, at full
 *     size => HD directly.
 *   - IMPORTANT: we load by SWAPPING IN A FRESH IFRAME with the src set before
 *     insertion. A newly-inserted iframe loads as a history "replace", so it
 *     does NOT add a browser-history entry. (Assigning to an existing iframe's
 *     .src instead pushes a history entry, which is what makes the browser
 *     Back button require two presses.)
 *   - Warm the connection to YouTube (preconnect) and prefetch the HD poster
 *     so the reveal is quick.
 *
 * Scope / safety:
 *   - Targets ONLY YouTube /embed/ iframes. Vimeo, HTML5 <video>, images,
 *     and everything else are left completely alone.
 *   - Iframes ALREADY visible at load are never touched.
 *   - Each iframe is activated at most once.
 *
 * Note: once a visitor actually PLAYS a video, YouTube's own player may add
 * history entries — that is YouTube's behavior, separate from this script.
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var YT_SELECTOR =
    'iframe[src*="youtube.com/embed"],' +
    'iframe[src*="youtube-nocookie.com/embed"]';

  var STATE = 'data-ythd';       // 'live' | 'deferred' | 'loaded'
  var SRC_STORE = 'data-ythd-src';
  var observer = null;

  // ---- Warm the connection to YouTube so the deferred load is quick --------
  function preconnect() {
    var hosts = [
      'https://www.youtube.com',
      'https://www.youtube-nocookie.com',
      'https://i.ytimg.com'
    ];
    for (var i = 0; i < hosts.length; i++) {
      var l = document.createElement('link');
      l.rel = 'preconnect';
      l.href = hosts[i];
      l.crossOrigin = '';
      document.head.appendChild(l);
    }
  }

  function isHidden(el) {
    return !el.offsetParent || el.offsetWidth < 2 || el.offsetHeight < 2;
  }

  function videoIdFromSrc(src) {
    var m = src && src.match(/\/embed\/([^/?&#]+)/);
    return m ? m[1] : null;
  }

  // Prefetch the HD poster so it is cached before reveal. Best-effort;
  // falls back to hqdefault when maxres does not exist.
  function prefetchPoster(id) {
    if (!id) return;
    var hi = new Image();
    hi.onerror = function () {
      var fb = new Image();
      fb.src = 'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg';
    };
    hi.src = 'https://i.ytimg.com/vi/' + id + '/maxresdefault.jpg';
  }

  function activate(iframe) {
    if (iframe.getAttribute(STATE) !== 'deferred') return;
    if (isHidden(iframe)) return;          // wait until it truly has a box
    if (!iframe.parentNode) return;        // detached; nothing to do

    var src = iframe.getAttribute(SRC_STORE);
    if (!src) {
      iframe.setAttribute(STATE, 'loaded');
      if (observer) observer.unobserve(iframe);
      return;
    }

    // Build a fresh iframe with the src set BEFORE insertion. A newly-inserted
    // iframe performs its first load as a history "replace", so it adds NO
    // browser-history entry — which is what keeps the Back button working on
    // the first press. cloneNode(false) copies all attributes (class, style,
    // width/height, allow, allowfullscreen, title, etc.).
    var fresh = iframe.cloneNode(false);
    fresh.removeAttribute(SRC_STORE);
    fresh.setAttribute(STATE, 'loaded');
    fresh.setAttribute('loading', 'eager');
    fresh.setAttribute('src', src);

    if (observer) observer.unobserve(iframe);
    iframe.parentNode.replaceChild(fresh, iframe);
  }

  function activateVisibleDeferred() {
    var frames = document.querySelectorAll('iframe[' + STATE + '="deferred"]');
    for (var i = 0; i < frames.length; i++) activate(frames[i]);
  }

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) activate(entries[i].target);
    }
  }

  function process(iframe) {
    if (!iframe || iframe.getAttribute(STATE)) return;

    if (!isHidden(iframe)) {
      // Visible at load => already loads HD on its own. Leave it untouched.
      iframe.setAttribute(STATE, 'live');
      return;
    }

    // Hidden at load => never let the blurry poster load. Park the real src
    // and blank the iframe; restore it (once) when the container is revealed.
    var src = iframe.getAttribute('src');
    iframe.setAttribute(SRC_STORE, src);
    iframe.removeAttribute('src'); // blank iframe keeps its width/height box
    iframe.setAttribute(STATE, 'deferred');
    prefetchPoster(videoIdFromSrc(src));
    if (observer) observer.observe(iframe);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var frames = scope.querySelectorAll(YT_SELECTOR);
    for (var i = 0; i < frames.length; i++) process(frames[i]);
  }

  function init() {
    preconnect();

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(onIntersect, {
        root: null,
        rootMargin: '200px',
        threshold: 0.01
      });
    }

    scan(document);

    // Start the load off the reveal click itself, a touch earlier than the
    // observer would deliver it. Runs on the next frame so the panel has been
    // laid out and the iframe has a real size. Idempotent (activate() guards).
    document.addEventListener(
      'click',
      function () {
        window.requestAnimationFrame(activateVisibleDeferred);
      },
      true
    );

    // Catch iframes injected after load (quick-view drawers, lazy sections…).
    if ('MutationObserver' in window && document.body) {
      var mo = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches(YT_SELECTOR)) process(node);
            else if (node.querySelectorAll) scan(node);
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
